<script setup lang="ts">
type ButtonTheme = "primary" | "secondary" | "danger" | "ghost";

type Props = {
  type?: "button" | "submit" | "reset";
  theme?: ButtonTheme;
  outline?: boolean;
  disabled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  type: "button",
  theme: "primary",
  outline: false,
  disabled: false,
});
</script>

<template>
  <button
    class="pix-button"
    :type="props.type"
    :disabled="props.disabled || undefined"
    :data-theme="props.theme"
    :data-outline="props.outline || undefined"
  >
    <slot />
  </button>
</template>

<style>
@layer pix-components {
  .pix-button {
    --_pix-button-bg: var(--pix-button-bg, var(--color-primary));
    --_pix-button-text: var(--pix-button-text, var(--color-text-on-primary));
    --_pix-button-border: var(--pix-button-border, var(--color-primary));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, var(--color-primary-hover));
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-text-on-primary));
    --_pix-button-border-hover: var(--pix-button-border-hover, var(--_pix-button-bg-hover));
    --_pix-button-radius: var(--pix-button-radius, var(--radius-small));
    --_pix-button-padding-x: var(--pix-button-padding-x, var(--spacing-small));
    --_pix-button-padding-y: var(--pix-button-padding-y, var(--spacing-small));

    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--_pix-button-padding-y) var(--_pix-button-padding-x);
    border: var(--border-thin) solid var(--_pix-button-border);
    border-radius: var(--_pix-button-radius);
    background: var(--_pix-button-bg);
    color: var(--_pix-button-text);
    transition:
      background var(--duration-normal),
      color var(--duration-normal),
      border-color var(--duration-normal);
    cursor: pointer;

    &:disabled,
    &[data-disabled="true"] {
      opacity: var(--opacity-disabled-state);
      pointer-events: none;
      cursor: default;
    }

    &:hover:not(:disabled, [data-disabled="true"]) {
      background: var(--_pix-button-bg-hover);
      color: var(--_pix-button-text-hover);
      border-color: var(--_pix-button-border-hover);
    }

    /* This component had NO focus style at all (WCAG 2.4.7): keyboard users got only the UA
       default, which several consumers then removed by restyling border/box-shadow. Drawn from the
       library-wide --focus-ring-* tokens (tokens/foundations.css) as an outline, not a box-shadow. */
    &:focus-visible {
      outline: var(--focus-ring-width) solid var(--focus-ring-color);
      outline-offset: var(--focus-ring-offset);
    }
  }

  .pix-button[data-theme="secondary"] {
    --_pix-button-bg: var(--pix-button-bg, var(--color-secondary));
    --_pix-button-border: var(--pix-button-border, var(--color-secondary));
    --_pix-button-text: var(--pix-button-text, var(--color-text-on-secondary));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, var(--color-secondary-hover));
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-text-on-secondary));
  }

  .pix-button[data-outline] {
    --_pix-button-bg: var(--pix-button-bg, transparent);
    --_pix-button-border: var(--pix-button-border, var(--color-primary-outline));
    --_pix-button-text: var(--pix-button-text, var(--color-primary-outline));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, var(--color-primary));
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-text-on-primary));
    --_pix-button-border-hover: var(--pix-button-border-hover, var(--color-primary-outline-hover));
  }

  .pix-button[data-theme="ghost"] {
    --_pix-button-bg: var(--pix-button-bg, transparent);
    --_pix-button-border: var(--pix-button-border, transparent);
    --_pix-button-text: var(--pix-button-text, var(--color-text));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, transparent);
    --_pix-button-border-hover: var(--pix-button-border-hover, transparent);
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-primary-hover));
  }

  .pix-button[data-theme="secondary"][data-outline] {
    --_pix-button-bg: var(--pix-button-bg, transparent);
    --_pix-button-border: var(--pix-button-border, var(--color-secondary));
    --_pix-button-text: var(--pix-button-text, var(--color-secondary));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, transparent);
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-secondary-hover));
    --_pix-button-border-hover: var(--pix-button-border-hover, var(--color-secondary-hover));
  }

  .pix-button[data-theme="danger"] {
    --_pix-button-bg: var(--pix-button-bg, var(--color-danger));
    --_pix-button-border: var(--pix-button-border, var(--color-danger));
    --_pix-button-text: var(--pix-button-text, var(--color-white));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, var(--color-danger-hover));
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-white));
    --_pix-button-border-hover: var(--pix-button-border-hover, var(--color-danger-hover));
  }

  .pix-button[data-theme="danger"][data-outline] {
    --_pix-button-bg: var(--pix-button-bg, transparent);
    --_pix-button-border: var(--pix-button-border, var(--color-danger));
    --_pix-button-text: var(--pix-button-text, var(--color-danger));
    --_pix-button-bg-hover: var(--pix-button-bg-hover, var(--color-danger));
    --_pix-button-text-hover: var(--pix-button-text-hover, var(--color-white));
    --_pix-button-border-hover: var(--pix-button-border-hover, var(--color-danger-hover));
  }
}
</style>
